import React, { useState } from "react";
import {
  MessageSquare,
  TrendingUp,
  Users,
  BarChart3,
} from "lucide-react";
import { Header } from "../../components/marketing/cpas-platform/header";
import { PathCard } from "../../components/marketing/cpas-platform/path-card";
import { TourStep } from "./types";
import { TourScreen } from "./tour";
import { ActiveCheckIcon, ActiveInputIcon, InactiveCheckIcon, InactiveInputIcon } from "../../assets/icon";
import { useNavigate } from "react-router-dom";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  token?: string;
  onNavigate?: (path: string) => void;
}

const ConvoHubPlatformModal: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const [showTour, setShowTour] = useState(false);
  const [currentTourStep, setCurrentTourStep] = useState(1);
  const [activePath, setActivePath] = useState<"marketing" | "crm">("marketing");
  const [isSettingUp, setIsSettingUp] = useState(false);
  const navigate = useNavigate();



  const tourSteps: TourStep[] = [
    {
      id: 1,
      title: "Welcome to Your CPaaS Platform",
      description:
        "Let me show you around your new unified communication platform",
      icon: <MessageSquare className="w-8 h-8 text-white" />,
      features: ["Unified dashboard", "Real-time insights", "Smart automation"],
    },
    {
      id: 2,
      title: "Marketing Automation",
      description:
        "Create targeted campaigns that convert with intelligent automation",
      icon: <TrendingUp className="w-8 h-8 text-white" />,
      features: ["Multi-channel campaigns", "Behavioral triggers"],
    },
    {
      id: 3,
      title: "CRM & Customer Management",
      description:
        "Manage relationships and track every interaction seamlessly",
      icon: <Users className="w-8 h-8 text-white" />,
      features: ["Contact management", "Sales pipeline"],
    },
    {
      id: 4,
      title: "Analytics & Insights",
      description:
        "Make data-driven decisions with comprehensive reporting",
      icon: <BarChart3 className="w-8 h-8 text-white" />,
      features: ["Real-time dashboards", "AI recommendations"],
    },
  ];

  const handleStartTour = () => {
    setShowTour(true);
    setCurrentTourStep(1);
  };

  const handleNextStep = () => {
    if (currentTourStep < tourSteps.length) {
      setCurrentTourStep((prev) => prev + 1);
    } else {
      setShowTour(false);
      setCurrentTourStep(1);
    }
  };

  const handlePreviousStep = () => {
    if (currentTourStep > 1) {
      setCurrentTourStep((prev) => prev - 1);
    }
  };

  const marketingFeatures = [
    "Campaign Management",
    "Journey Builder",
    "Data Onboarding",
    "Unified Customer Profile Builder",
  ];

  const crmFeatures = [
    "Unified Inbox",
    "Contact Management",
    "Sales Pipeline",
    "Activity Tracking",
    "Reporting",
  ];

  const handleCloseTour = () => {
    setShowTour(false);
    setCurrentTourStep(1);
  };

  if (!isOpen) return null;



  const handlePathSelect = (path: "marketing" | "crm") => {
    setActivePath(path);
    setIsSettingUp(true);

    setTimeout(() => {
      if (path === "marketing") {
        navigate("/cpas-dashboard");
      } else {
        navigate("/convoAi");
      }
    }, 5000);
  };


  // TOUR VIEW
  if (showTour) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="relative w-full max-w-4xl">
          <TourScreen
            step={tourSteps[currentTourStep - 1]}
            currentStep={currentTourStep}
            totalSteps={tourSteps.length}
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
            onClose={handleCloseTour}
            assistantMessage="Let me guide you through the platform."
          />
        </div>
      </div>
    );
  }

  if (isSettingUp) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-10 text-center shadow-2xl w-[400px]">
          <h2 className="text-xl font-semibold mb-3">
            Setting things up...
          </h2>

          <p className="text-gray-600 mb-6">
            We’re preparing your workspace. This won’t take long.
          </p>

          {/* Loader */}
          <div className="flex justify-center">
            <div className="w-6 h-6 border-4 border-gray-300 border-t-[#211C64] rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }


  // MAIN MODAL
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white lg:w-[760px] w-[500px] max-w-6xl rounded-2xl shadow-2xl overflow-y-auto max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8">
          <Header onStartTour={handleStartTour} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section
              onClick={() => handlePathSelect("marketing")}

              className={`rounded-2xl border transition-all
    ${activePath === "marketing"
                  ? "bg-gradient-to-br from-[#EDEDFA] to-[#FDFCFD] hover:border-[#211C64] hover:border-4"
                  : "bg-gradient-to-br from-[#EDEDFA] to-[#FDFCFD] border-gray-200"
                }`}
            >
              <PathCard
                isActive={activePath === "marketing"}
                activeIcon={<ActiveInputIcon />}
                inactiveIcon={<InactiveInputIcon />}
                activeCheckIcon={<ActiveCheckIcon />}
                inactiveCheckIcon={<InactiveCheckIcon />}
                title="Marketing"
                description="Drive growth with intelligent campaigns, automation and analytics."
                features={marketingFeatures}
              />
            </section>

            <section
              onClick={() => handlePathSelect("crm")}

              className={`rounded-2xl border transition-all
    ${activePath === "crm"
                  ? "bg-[#FBF9F2] border-[#211C64] border-4"
                  : "bg-[#FBF9F2] border-gray-200"
                }`}
            >
              <PathCard
                isActive={activePath === "crm"}
                activeIcon={<ActiveInputIcon />}
                inactiveIcon={<InactiveInputIcon />}
                activeCheckIcon={<ActiveCheckIcon />}
                inactiveCheckIcon={<InactiveCheckIcon />}
                title="CRM"
                description="Manage customer relationships and streamline your sales process."
                features={crmFeatures}

              />
            </section>
          </div>
        </div>
      </div>
    </div>
  );

};

export default ConvoHubPlatformModal;
